import React from 'react';

import CurrencyInput from 'react-currency-input-field';
import InputMask from 'react-input-mask';

import { isDependencyCheckFails } from './isDependencyCheckFails';

import { formatNumber } from '../../../helpers/format';
import { validateInputField } from '../../../helpers/validation';
import {
  AddressInput,
  CustomCheckbox,
  FileInput,
  FormTextArea,
  GeneralDatePicker,
  LabelWithTooltip,
  MultipleSelect,
  NaicsCode,
  ShortDatePicker,
  SingleSelect,
  SelectOneWithFreeInput,
} from '../../FormElements';

export const RenderFieldFn = (field, renderFieldProps, owners, handleSaveFileProperty, fields) => {
  const {
    data,
    setData,
    dataError,
    setDataError,
    toggleFieldModal,
    isModalOpen,
    initializeSectionError,
    maskInputEl,
  } = renderFieldProps;

  if (!isDependencyCheckFails(field, data, fields)) {
    return '';
  }
  const fieldClassName = `form-group ${field.style ? field.style : ''} ${
    dataError[field.name] ? 'error' : ''
  } ${field.dependsOn ? 'indent' : ''}`;

  const fieldProps = {
    ...renderFieldProps,
    field,
    fieldClassName,
  };
  // Handler for currency input
  const handleCurrenyChange = (value, name, field) => {
    initializeSectionError();

    if (value > field.limits.maximum) {
      let fixedValue = '';
      if (value.includes('.')) {
        const arrNumberStr = value.split('.');
        fixedValue =
          arrNumberStr[0].substring(0, arrNumberStr[0].length - 1) + '.' + arrNumberStr[1];
      } else {
        fixedValue = value.substring(0, value.length - 1);
      }

      setData({
        ...data,
        [name]: fixedValue,
      });
      return false;
    }
    setData({ ...data, [name]: value });
  };

  // update files state for FileInput
  const handleSetData = (field, values) => {
    setData((data) => ({
      ...data,
      [field.name]: Array.isArray(values) ? [...values] : values,
    }));
  };

  const handleSetDataError = (field, msg) => {
    setDataError({ ...dataError, [field.name]: msg });
  };
  // Handler for form field change
  const handleChange = (e, name, field) => {
    // Initialize the Section Error.
    initializeSectionError();
    validateInputField(e.target.value, name, field, setDataError);
    if (field.type.includes('numberInput')) {
      const textValue = formatNumber(field.type, e.target.value);
      if (!textValue) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'This field should not be empty.',
        }));
      }
      const numberValue = parseInt(e.target.value.replace(/,/g, ''));
      setData({ ...data, [name]: textValue });
      if (numberValue > field.limits.maximum) {
        e.preventDefault();
        setData({
          ...data,
          [name]: formatNumber(field.type, textValue.substring(0, textValue.length - 1)),
        });
        return false;
      }
    } else if (field.type === 'checkbox') {
      setData({ ...data, [name]: e.target.checked });
    } else if (field.type === 'email' && owners) {
      const duplicateOwner = owners?.find((owner) => owner.email === e.target.value);
      if (duplicateOwner) {
        setDataError((dataError) => ({
          ...dataError,
          [name]: 'Same Email already exists.',
        }));
        return false;
      } else {
        setData({ ...data, [name]: e.target.value });
        setDataError((dataError) => ({
          ...dataError,
          [name]: '',
        }));
      }
    } else if ((name === 'firstName' || name === 'lastName') && owners) {
      let duplicateOwner;
      if (name === 'firstName' && data?.lastName) {
        duplicateOwner = owners?.find(
          (owner) => owner.firstName + ' ' + owner.lastName === e.target.value + ' ' + data.lastName
        );
      } else if (name === 'lastName') {
        duplicateOwner = owners?.find(
          (owner) =>
            owner.firstName + ' ' + owner.lastName === data.firstName + ' ' + e.target.value
        );
      }

      if (duplicateOwner) {
        setDataError((dataError) => ({
          ...dataError,
          firstName: 'This user was already added.',
          lastName: 'This user was already added.',
        }));
        return false;
      } else {
        setData({ ...data, [name]: e.target.value });
        setDataError((dataError) => ({
          ...dataError,
          firstName: '',
          lastName: '',
        }));
      }
    } else {
      setData({ ...data, [name]: e.target.value });
    }
  };

  if (field.type === 'naicsCode') {
    return <NaicsCode key={field.name} {...fieldProps} handleChange={handleChange} />;
  }

  if (field.type === 'selectOne') {
    return <SingleSelect key={field.name} {...fieldProps} />;
  }
  if (field.type === 'selectOneWithFreeInput') {
    return <SelectOneWithFreeInput key={field.name} {...fieldProps} />;
  }

  if (field.type === 'selectMultiple') {
    return <MultipleSelect key={field.name} {...fieldProps} />;
  }

  if (field.type === 'address') {
    return <AddressInput key={field.name} {...fieldProps} isShowFieldPrefix={true} />;
  }

  if (field.type === 'maskInput') {
    return (
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
          <InputMask
            id={field.name}
            name={field.name}
            mask={field.mask}
            ref={maskInputEl}
            value={data[field.name] ? data[field.name] : field.defaultValue}
            onChange={(e) => handleChange(e, field.name, field)}
            placeholder={field.placeholder}
            spellCheck={false}
          />
        </div>
        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>
    );
  }

  if (field.type === 'email') {
    return (
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
          <input
            id={field.name}
            value={data[field.name]}
            name={field.name}
            onChange={(e) => handleChange(e, field.name, field)}
            type={field.type}
            spellCheck={false}
          />
        </div>
        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>
    );
  }

  if (field.type === 'date') {
    return <GeneralDatePicker key={field.name} {...fieldProps} />;
  }
  if (field.type === 'shortDate') {
    return <ShortDatePicker key={field.name} {...fieldProps} />;
  }

  if (field.type === 'file') {
    if (field.name.includes('bankVerificationStatement')) {
      /** Don't render bank verification file uploads */
      return;
    }
    return (
      <FileInput
        field={field}
        key={field.name}
        fieldValue={data[field.name] ?? []}
        fieldError={dataError[field.name]}
        handleSetData={handleSetData}
        handleSetDataError={handleSetDataError}
        handleSaveFileProperty={handleSaveFileProperty}
        initializeSectionError={initializeSectionError}
      />
    );
  }

  if (field.type === 'checkbox') {
    return <CustomCheckbox key={field.name} {...fieldProps} handleChange={handleChange} />;
  }

  if (field.type === 'hidden') {
    return (
      <input
        id={field.name}
        key={field.name}
        value={data[field.name]}
        name={field.name}
        onChange={(e) => handleChange(e, field.name, field)}
        type={'hidden'}
        className={dataError[field.name] ? 'error' : ''}
        spellCheck={false}
      />
    );
  }

  if (field.type === 'dollarInput') {
    return (
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
          <CurrencyInput
            id={field.name}
            name={field.name}
            placeholder={field.placeholder}
            value={data[field.name]}
            decimalScale={2}
            onValueChange={(value, name) => handleCurrenyChange(value, name, field)}
            className={dataError[field.name] ? 'error' : ''}
            intlConfig={{ locale: 'en-US', currency: 'USD' }}
            spellCheck={false}
          />
        </div>

        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <FormTextArea
        key={field.name}
        {...fieldProps}
        handleChange={handleChange}
        spellCheck={false}
      />
    );
  }

  if (field.type === 'simpleLabel') {
    // Custom condition for adding and displaying a sum of all expenses
    // Special reserved field name 'totalExpenses' is used
    if (field.name === 'totalExpenses') {
      let totalExpenseSum = 0;
      for (let dataIndex in data) {
        if (Number(data[dataIndex] > 0)) {
          totalExpenseSum += Number(data[dataIndex]);
        }
      }
      field.label = 'Total Expenses: $' + totalExpenseSum;
    }

    return (
      <div key={field.name} className={fieldClassName}>
        <div className="form-field">
          <LabelWithTooltip
            field={field}
            toggleFieldModal={toggleFieldModal}
            isModalOpen={isModalOpen}
          />
        </div>

        {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
      </div>
    );
  }

  return (
    <div key={field.name} className={fieldClassName}>
      <div className="form-field">
        <LabelWithTooltip
          field={field}
          toggleFieldModal={toggleFieldModal}
          isModalOpen={isModalOpen}
        />
        <input
          id={field.name}
          value={data[field.name]}
          name={field.name}
          onChange={(e) => handleChange(e, field.name, field)}
          type={field.type}
          className={dataError[field.name] ? 'error' : ''}
          placeholder={field.placeholder}
          spellCheck={false}
        />
      </div>

      {dataError[field.name] && <p className="error-text">{dataError[field.name]}</p>}
    </div>
  );
};
